// [PURPOSE]: Error Boundary - Catch React errors and prevent app crashes
// [WHY]: Implement proper error handling to prevent white screen crashes
// [INTEGRATION]: Can wrap entire app OR individual components
// [CREATED]: October 24, 2025 - Phase 4A: Architectural Refactoring
// [ENHANCED]: November 12, 2025 - Phase 4.3: Activity logging, component-specific errors
// [ENHANCED]: April 2026 - Session 6: Sentry error reporting + analytics tracking

import React, { Component } from 'react';
import * as Sentry from '@sentry/react';
import { trackEvent } from '../services/analytics';

/**
 * ErrorBoundary component catches errors in child components
 *
 * Features:
 * - Catches errors during rendering, lifecycle methods, and constructors
 * - Displays fallback UI when error occurs (custom or default)
 * - Logs error details to console AND activity log
 * - Provides reset mechanism to recover
 * - Prevents entire app from crashing
 * - Supports component-specific error handling
 *
 * Props:
 * @param {React.ReactNode} children - Components to wrap
 * @param {React.ReactNode} fallback - Custom fallback UI (optional)
 * @param {string} componentName - Name of wrapped component (for logging)
 * @param {function} logActivity - Activity logging function (optional)
 *
 * Usage:
 * // Wrap entire app:
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 *
 * // Wrap individual component:
 * <ErrorBoundary
 *   componentName="VitalStatsCard"
 *   logActivity={logActivity}
 *   fallback={<ComponentErrorFallback componentName="VitalStatsCard" />}
 * >
 *   <VitalStatsCard {...props} />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  /**
   * Update state when error occurs
   * This lifecycle method is called during the "render" phase
   * @param {Error} error - The error that was thrown
   * @returns {Object} New state
   */
  static getDerivedStateFromError(error) {
    // Update state so next render shows fallback UI
    return {
      hasError: true,
      error
    };
  }

  /**
   * Log error details
   * This lifecycle method is called during the "commit" phase
   * @param {Error} error - The error that was thrown
   * @param {Object} errorInfo - Component stack trace
   */
  componentDidCatch(error, errorInfo) {
    const { componentName, logActivity } = this.props;

    // Log error details to console
    console.error('❌ Error Boundary caught an error:', error);
    if (componentName) {
      console.error(`Component: ${componentName}`);
    }
    console.error('Component Stack:', errorInfo.componentStack);

    // Update state with error details
    this.setState(prevState => ({
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // [PHASE 4.3]: Log to activity feed if logActivity function provided
    if (logActivity && typeof logActivity === 'function') {
      try {
        const componentLabel = componentName || 'Unknown Component';
        logActivity(
          `❌ Error in ${componentLabel}: ${error.message}`,
          'error',
          {
            component: componentLabel,
            error: error.toString(),
            errorMessage: error.message,
            errorName: error.name,
            stack: error.stack,
            componentStack: errorInfo.componentStack
          }
        );
      } catch (loggingError) {
        // Don't let logging errors crash the error handler
        console.error('Failed to log error to activity feed:', loggingError);
      }
    }

    // [S6]: Report to Sentry with component context for triage.
    // The outermost Sentry.ErrorBoundary (index.js) catches catastrophic failures;
    // this captures component-level errors that the inner ErrorBoundary handles gracefully.
    try {
      Sentry.withScope((scope) => {
        if (componentName) {
          scope.setTag('component', componentName);
        }
        scope.setExtra('componentStack', errorInfo.componentStack);
        Sentry.captureException(error);
      });
    } catch {
      // [PURPOSE]: Sentry reporting must never make the error boundary itself fail.
    }

    // [S6]: Track error_boundary_triggered in analytics for crash-rate metrics.
    trackEvent('error_boundary_triggered', {
      component: componentName || 'unknown',
      error_name: error?.name || 'Error',
      error_message: (error?.message || '').slice(0, 100), // Truncate for analytics
    });
  }

  /**
   * Reset error boundary and try to recover
   */
  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  /**
   * Reload the entire page (last resort)
   */
  handleReload = () => {
    window.location.reload();
  };

  /**
   * Copy error details to clipboard with enhanced debugging info
   */
  handleCopyError = () => {
    // [FIX - NOV 3 SESSION 6]: Add more debugging context
    const errorText = `Error: ${this.state.error?.toString() || 'Unknown error'}

Error Type: ${this.state.error?.name || 'Unknown'}
Error Message: ${this.state.error?.message || 'No message'}

Stack Trace:
${this.state.error?.stack || 'No stack trace'}

Component Stack:
${this.state.errorInfo?.componentStack || 'No component stack'}

Context State at Crash:
- localStorage keys: ${Object.keys(localStorage).join(', ')}
- localStorage size: ${JSON.stringify(localStorage).length} bytes
- Window size: ${window.innerWidth}x${window.innerHeight}
- User agent: ${navigator.userAgent}
`;

    // Try modern clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(errorText)
        .then(() => alert('Error copied to clipboard!'))
        .catch(() => this.fallbackCopy(errorText));
    } else {
      this.fallbackCopy(errorText);
    }
  };

  /**
   * Fallback copy method for older browsers/WebView
   */
  fallbackCopy = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      alert('Error copied to clipboard!');
    } catch (err) {
      alert('Failed to copy. Please take a screenshot.');
    }

    document.body.removeChild(textArea);
  };

  /**
   * Clear localStorage and reload (nuclear option)
   */
  handleClearAndReload = () => {
    // [UI-CONFIRM-UNIFICATION 2026-07-04]: PRINCIPLED EXCEPTION — this stays
    // window.confirm. ErrorBoundary is a class component that renders when
    // the React tree has ALREADY crashed; useConfirm/ConfirmDialog may be
    // part of the broken tree. Native confirm is the only reliable UI here.
    if (window.confirm('This will clear all saved data and reload the app. Are you sure?')) {
      localStorage.clear();
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      // [PHASE 4.3]: If custom fallback provided, use it (for component-specific errors)
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Otherwise, render default full-screen fallback UI (for app-level errors)
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-900 via-gray-900 to-gray-800 flex items-center justify-center p-4">
          <div className="bg-gray-800 border-2 border-red-500 rounded-lg shadow-2xl max-w-2xl w-full p-8">
            {/* Error Icon */}
            <div className="flex items-center justify-center mb-6">
              <div className="bg-red-500 rounded-full p-4">
                <svg
                  className="w-12 h-12 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>

            {/* Error Title */}
            <h1 className="text-3xl font-bold text-red-400 text-center mb-4">
              Oops! Something went wrong
            </h1>

            {/* Error Description */}
            <p className="text-gray-300 text-center mb-6">
              The application encountered an unexpected error. Don't worry - your data should be safe.
            </p>

            {/* Error Details (Always Visible) */}
            {/* [NOV 3 FIX]: Changed from collapsible to always visible + copy button */}
            {this.state.error && (
              <div className="mb-6 bg-gray-900 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-red-400 font-semibold">Error Details (for debugging)</p>
                  <button
                    onClick={this.handleCopyError}
                    className="bg-gray-700 hover:bg-gray-600 text-white text-xs font-semibold py-2 px-4 rounded transition-colors"
                  >
                    📋 Copy Error
                  </button>
                </div>
                <div className="mt-2 text-xs text-gray-400 font-mono overflow-auto max-h-64" style={{userSelect: 'text', WebkitUserSelect: 'text', MozUserSelect: 'text'}}>
                  <p className="text-red-300 font-bold mb-2" style={{userSelect: 'text'}}>Error Message:</p>
                  <p className="mb-4 break-all whitespace-pre-wrap" style={{userSelect: 'text', WebkitUserSelect: 'text', wordBreak: 'break-word'}}>{this.state.error.toString()}</p>

                  {this.state.errorInfo && (
                    <>
                      <p className="text-red-300 font-bold mb-2" style={{userSelect: 'text'}}>Component Stack:</p>
                      <pre className="whitespace-pre-wrap break-all" style={{userSelect: 'text', WebkitUserSelect: 'text', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '11px'}}>{this.state.errorInfo.componentStack}</pre>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Error Count Warning */}
            {this.state.errorCount > 1 && (
              <div className="bg-yellow-900 border border-yellow-500 rounded-lg p-4 mb-6">
                <p className="text-yellow-300 text-sm">
                  ⚠️ This error has occurred {this.state.errorCount} times. If the problem persists,
                  try clearing your data below.
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Try Again Button */}
              <button
                onClick={this.handleReset}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors shadow-lg"
              >
                🔄 Try Again
              </button>

              {/* Reload Page Button */}
              <button
                onClick={this.handleReload}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors shadow-lg"
              >
                🔃 Reload Page
              </button>

              {/* Clear Data Button (only show after multiple errors) */}
              {this.state.errorCount > 2 && (
                <button
                  onClick={this.handleClearAndReload}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors shadow-lg"
                >
                  🗑️ Clear Data & Reload
                </button>
              )}
            </div>

            {/* [PHASE 6 FIX]: Removed incorrect GitHub reference per user request */}
          </div>
        </div>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
